export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8010';

const SESSION_STORAGE_KEY = 'bsr-sysreg-session';

export class ApiError extends Error {
  constructor({ message, code, details, status, raw }) {
    super(message || 'API request failed');
    this.name = 'ApiError';
    this.code = code || 'API_ERROR';
    this.details = details || [];
    this.status = status || 500;
    this.raw = raw;
  }
}

export function buildQuery(params = {}) {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '' || value === false) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== '') {
          searchParams.append(key, String(item));
        }
      });
      return;
    }

    searchParams.set(key, String(value));
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

export function toApiUrl(path) {
  if (!path) return API_BASE_URL;
  if (/^https?:\/\//.test(path)) return path;
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

function normalizeBody(body, headers) {
  if (body === undefined || body === null) {
    return undefined;
  }

  if (body instanceof FormData) {
    headers.delete('Content-Type');
    return body;
  }

  if (typeof body === 'string') {
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'text/plain');
    }
    return body;
  }

  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  return JSON.stringify(body);
}

async function parseResponse(response) {
  const text = await response.text();
  let data = null;

  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      const statusHint = {
        502: 'เซิร์ฟเวอร์เชื่อมต่อ bsr.env ไม่ได้ — ตรวจ PHASE_API_BASE_URL และ token ฝั่ง backend',
        503: 'บริการ bsr.env ยังไม่ได้ตั้งค่าบนเซิร์ฟเวอร์ — ติดต่อ IT Admin',
        404: 'ไม่พบ API endpoint — อาจยังไม่ได้ deploy backend เวอร์ชันล่าสุด',
      }[response.status];

      throw new ApiError({
        message: statusHint || `เซิร์ฟเวอร์ตอบกลับไม่ถูกต้อง (HTTP ${response.status})`,
        code: 'BAD_RESPONSE',
        status: response.status,
        raw: text,
      });
    }
  }

  if (!response.ok || data?.success === false) {
    throw new ApiError({
      message: data?.error?.message || data?.message || response.statusText,
      code: data?.error?.code,
      details: data?.error?.details,
      status: response.status,
      raw: data,
    });
  }

  return data ?? { success: true, data: null };
}

export async function apiRequest(path, options = {}) {
  const { method = 'GET', headers, body, accessToken, signal } = options;
  const requestHeaders = new Headers(headers || {});

  if (accessToken) {
    requestHeaders.set('Authorization', `Bearer ${accessToken}`);
  }

  const requestBody = normalizeBody(body, requestHeaders);

  const response = await fetch(toApiUrl(path), {
    method,
    headers: requestHeaders,
    body: requestBody,
    signal,
  });

  return parseResponse(response);
}

export function loadStoredSession() {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveStoredSession(session) {
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

export function clearStoredSession() {
  localStorage.removeItem(SESSION_STORAGE_KEY);
}

const API_ERROR_HINTS = {
  PHASE_NOT_CONFIGURED: 'ยังไม่ได้ตั้งค่า bsr.env บนเซิร์ฟเวอร์ — ติดต่อ IT Admin ให้ใส่ PHASE_SERVICE_ACCOUNT_TOKEN',
  PHASE_AUTH_FAILED: 'bsr.env ปฏิเสธ token — ตรวจ Service Account หรือเชิญเข้า App นี้ใน bsr.env',
  PHASE_UNAVAILABLE: 'เชื่อมต่อ bsr.env ไม่ได้ — ตรวจ PHASE_API_BASE_URL (https://bsr-env.boonsiri.co.th/service/public)',
  PHASE_NO_ENVIRONMENTS: 'พบ App ใน bsr.env แต่ยังไม่มี Environment — สร้าง Dev/Staging/Prod ก่อน',
  NOT_FOUND: 'ไม่พบ App นี้ใน bsr.env — ตรวจ App ID หรือสิทธิ์ Service Account',
  BAD_RESPONSE: 'เซิร์ฟเวอร์ตอบกลับไม่ถูกต้อง — อาจยังไม่ได้ deploy backend หรือ bsr.env ล่ม',
};

const PHASE_VERIFY_DEFAULT = 'App ID ไม่ถูกต้อง — กรุณาตรวจสอบการสร้าง App และการเชื่อมต่อ Environment';

const PHASE_VERIFY_ERROR_HINTS = {
  NOT_FOUND: PHASE_VERIFY_DEFAULT,
  PHASE_NO_ENVIRONMENTS: 'พบ App แล้วแต่ยังไม่มี Environment — กรุณาสร้างและเชื่อมต่อ Environment ก่อนยืนยัน',
  PHASE_AUTH_FAILED: 'ไม่สามารถเข้าถึง App นี้ได้ — ตรวจสอบ App ID และสิทธิ์ Service Account ใน bsr.env',
  PHASE_NOT_CONFIGURED: API_ERROR_HINTS.PHASE_NOT_CONFIGURED,
  PHASE_UNAVAILABLE: PHASE_VERIFY_DEFAULT,
};

export function getPhaseVerifyErrorMessage(error, fallback = PHASE_VERIFY_DEFAULT) {
  if (error instanceof ApiError) {
    if (error.code === 'VALIDATION_ERROR') {
      return error.message || fallback;
    }
    if (error.code && PHASE_VERIFY_ERROR_HINTS[error.code]) {
      return PHASE_VERIFY_ERROR_HINTS[error.code];
    }
    if ([404, 409, 502].includes(error.status)) {
      return PHASE_VERIFY_DEFAULT;
    }
    return error.message || fallback;
  }

  if (error instanceof Error) {
    if (error.message === 'Failed to fetch') {
      return PHASE_VERIFY_DEFAULT;
    }
    return error.message || fallback;
  }

  return fallback;
}

export function getErrorMessage(error, fallback = 'เกิดข้อผิดพลาดจาก API') {
  if (error instanceof ApiError) {
    if (error.code && API_ERROR_HINTS[error.code]) {
      return error.message || API_ERROR_HINTS[error.code];
    }
    if (error.status === 502) {
      return error.message || API_ERROR_HINTS.PHASE_UNAVAILABLE;
    }
    return error.message || fallback;
  }

  if (error instanceof Error) {
    if (error.message === 'Failed to fetch') {
      return 'เชื่อมต่อ API ไม่ได้ — ตรวจเครือข่ายหรือว่า backend ทำงานอยู่';
    }
    return error.message || fallback;
  }

  return fallback;
}

const HR_API_URL = 'https://api-hr.boonsiri.co.th';

export async function loginRequest({ username, password }, signal) {
  const response = await fetch(`${HR_API_URL}/loginAd`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
    signal,
  });

  const data = await response.json();

  if (data.status === 'success') {
    return { data };
  }

  const msg = (data.message || '').toLowerCase();
  if (msg.includes('must reset password') || msg.includes('must change password')) {
    throw new ApiError({ message: data.message, code: 'MUST_RESET_PASSWORD', status: 403 });
  }
  if (msg.includes('disabled')) {
    throw new ApiError({ message: data.message, code: 'ACCOUNT_DISABLED', status: 403 });
  }
  if (msg.includes('expired')) {
    throw new ApiError({ message: data.message, code: 'PASSWORD_EXPIRED', status: 403 });
  }

  throw new ApiError({
    message: data.message || 'Invalid username or password',
    code: 'INVALID_CREDENTIALS',
    status: response.status,
  });
}

export async function verifySsoCodeRequest(code, signal) {
  const response = await fetch(`${HR_API_URL}/verifySsoCode`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
    signal,
  });

  const data = await response.json();

  if (data.status === 'success' && data.token) {
    return { token: data.token };
  }

  throw new ApiError({
    message: data.message || 'SSO verification failed',
    code: 'SSO_VERIFY_FAILED',
    status: response.status,
  });
}

export async function exchangeTokenRequest({ hrToken, username }, signal) {
  return apiRequest('/auth/exchange', {
    method: 'POST',
    body: { hrToken, username },
    signal,
  });
}

export async function refreshRequest(refreshToken, signal) {
  return apiRequest('/auth/refresh', {
    method: 'POST',
    body: { refreshToken },
    signal,
  });
}

export async function logoutRequest(accessToken, refreshToken, signal) {
  return apiRequest('/auth/logout', {
    method: 'POST',
    accessToken,
    body: { refreshToken },
    signal,
  });
}

export async function resetPasswordRequest({ mail, newPassword }, signal) {
  const response = await fetch(`${HR_API_URL}/forgetPasswordByMail`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mail, newPassword }),
    signal,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new ApiError({
      message: data.message || 'Reset password failed',
      code: 'RESET_PASSWORD_ERROR',
      status: response.status,
    });
  }

  return { data };
}

export async function loginMicrosoftRequest(idToken, signal) {
  return apiRequest('/auth/login-microsoft', {
    method: 'POST',
    body: { idToken },
    signal,
  });
}

export async function searchHrEmployees(search, signal) {
  const response = await fetch(`${HR_API_URL}/searchEmployees?search=${encodeURIComponent(search)}`, { signal });
  const result = await response.json();
  if (result.status !== 'success' || !Array.isArray(result.data)) return [];
  return result.data.map((emp) => ({
    userId: emp.employeeId,
    empId: emp.employeeId,
    empCode: emp.employeeCode,
    nameTh: emp.nameTh,
    fullName: `${emp.firstName} ${emp.lastName}`,
    nickname: emp.nickname,
    position: emp.position,
    department: emp.department,
    organization: emp.organization,
    email: emp.email,
    employeeStatus: emp.employeeStatus,
  }));
}

export async function fetchOrgWithPositions(signal) {
  const response = await fetch(`${HR_API_URL}/organization/all`, { signal });
  const result = await response.json();
  if (!response.ok || result.resCode !== '0000') {
    throw new ApiError({ message: result.resMessage || 'Failed to fetch positions', code: 'FETCH_POSITIONS_ERROR', status: response.status });
  }
  return result.data.filter((item) => item.isActive);
}
